import { useState, useEffect } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import ViewRequestsDialog from './ViewRequestsDialog';
import setupSockets, { onConnectionRequest, offConnectionRequest } from '../../../socket/socketSetup';

const Requests = () => {
  const [data, setData] = useState({ requests: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [showViewRequestDialog, setShowViewRequestDialog] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const token = useSelector((state) => state.auth.token);

  const fetchData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/notification/requests?userId=${user.id}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'Api-Name': 'user-api',
        },
      });
      const response_json = await response.json();
      setData(response_json);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Handle new connection requests
    const handleNewRequest = (requestData) => {
      console.log('Received new request:', requestData);
      setData(prevData => ({
        requests: [requestData, ...prevData.requests],
        users: [
          ...prevData.users,
          {
            id: requestData.requester.id,
            name: requestData.requester.name,
            imageUrl: requestData.requester.imageUrl,
            accountType: requestData.requester.accountType,
            username: requestData.requester.username
          }
        ]
      }));
    };

    // Register socket event listener
    onConnectionRequest(handleNewRequest);

    // Cleanup
    return () => {
      offConnectionRequest(handleNewRequest);
    };
  }, [user.id, token]);

  const updateViewed = async (req) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/notification/requests/${req.id}?viewed=true`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'Api-Name': 'user-api',
        },
      });
      const response_json = await response.json();
      if (response_json.status === 'success') {
        const updatedRequest = { ...req, viewed: true };
        const updatedRequests = data.requests.map(r => r.id === req.id ? updatedRequest : r);
        setData({ ...data, requests: updatedRequests });
      }
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-pri-yellow"></div>
      </div>
    );
  }

  if (!data.requests || data.requests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <h1 className="text-2xl font-bold text-pri-white mb-2">No Requests</h1>
        <p className="text-pri-gray">You don't have any pending requests at the moment.</p>
      </div>
    );
  }

  return (    
    <>
      <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 lg:flex md:ml-10 md:mt-1">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-pri-white dark:text-white sm:text-3xl sm:truncate align-center">Requests</h1>
          <div className="mt-1">
            <div className="flex flex-col">
              <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                  <ul className="divide-y divide-pri-dark">
                    {data.requests.map((req) => {
                      const reqUser = data.users.find(u => u.id === req.requesterId);
                      return (
                        <li key={req.id} 
                          onClick={() => {
                            if (!req.viewed) {
                              updateViewed(req);
                            }
                            setSelectedRequest(req);
                            setSelectedUser(reqUser);
                            setShowViewRequestDialog(true);
                          }}
                          className={`relative py-2 cursor-pointer hover:bg-ter-dark border-pri-yellow hover:b-pri-green hover:rounded-xl rounded-3xl ${!req.viewed ? 'bg-sec-dark' : ''}`}
                        >
                          <div className="px-4 sm:px-6 lg:px-8">
                            <div className="mx-auto flex max-w-4xl justify-between gap-x-6">
                              <div className="flex min-w-0 gap-x-4">
                                {!req.viewed && (
                                  <div className="h-4 w-4 bg-pri-green rounded-full mt-4"></div>
                                )}
                                <img 
                                  alt="" 
                                  src={reqUser?.imageUrl || reqUser?.profile_photo_url} 
                                  className="h-12 w-12 flex-none rounded-full bg-gray-50" 
                                />
                                <div className="min-w-0 flex-auto">
                                  <p className="text-sm font-semibold leading-6 text-pri-white">
                                    {reqUser?.name}
                                  </p>
                                  {req.date && (
                                    <p className="mt-1 text-xs leading-5 text-pri-gray">
                                      <time dateTime={req.date}>
                                        {format(parseISO(req.date), 'MMM d, yyyy h:mm a')}
                                      </time>
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex shrink-0 items-center gap-x-4">
                                <div className="hidden sm:flex sm:flex-col sm:items-end">
                                  <p className="text-sm leading-6 text-pri-white border border-pri-green p-1 rounded-xl">
                                    {reqUser?.accountType || 'User'}
                                  </p>
                                </div>
                                <ChevronRightIcon aria-hidden="true" className="h-5 w-5 flex-none text-pri-green" />
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>    
      {showViewRequestDialog && (
        <ViewRequestsDialog 
          setShowViewRequestDialog={setShowViewRequestDialog} 
          showViewRequestDialog={showViewRequestDialog} 
          selectedRequest={selectedRequest}
          selectedUser={selectedUser}
          data={data}
          setData={setData}
        />
      )}
    </>    
  );
}

export default Requests;